<template>
  <div class="verify-reporting-modal">
    <div class="modal-header">
      <h3>Verify Reporting ({{ rows.length }})</h3>
      <div class="header-actions">
        <button type="button" @click="testRunBilling" aria-label="Test Run Billing">Test Run Billing</button>
        <button type="button" @click="testRunReporting" aria-label="Test Run Reporting">Test Run Reporting</button>
        <select v-model="filterstatus" @change="fetchReports(filterstatus)" aria-label="Filter reports by status">
          <option value="not_verified">Not Verified</option>
          <option value="verified">Verified</option>
        </select>
        <input v-model="searchQuery" type="search" placeholder="Search reports" aria-label="Search reports" />
        <button class="close-btn" type="button" aria-label="Close" @click="$emit('close')">&times;</button>
      </div>
    </div>
    <div class="modal-body">
      <div v-if="isLoading" class="state-message">Loading...</div>
      <div v-else-if="error" class="state-message error-message">{{ error }}</div>
      <div v-else-if="filteredRows.length === 0" class="state-message">No verification reports found.</div>
      <div v-else class="table-wrapper">
        <table class="reports-table">
          <thead>
            <tr>
              <th class="selection-column"><input type="checkbox" :checked="allFilteredRowsSelected" @change="toggleSelectAll" aria-label="Select all reports" /></th>
              <th>#</th>
              <th v-for="column in columns" :key="column">{{ formatColumn(column) }}</th>
              <th class="actions-column"><button class="verify-button" type="button" @click="verifySelected" :disabled="selectedAutoIds.length === 0 || isVerifying">{{ verificationActionLabel }}</button></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in filteredRows" :key="row.autoid || index">
              <td class="selection-column"><input type="checkbox" :checked="isSelected(row)" :disabled="getRowId(row) === null" @change="toggleRow(row)" :aria-label="`Select report ${getRowId(row) || index + 1}`" /></td>
              <td>{{ index + 1 }}</td>
              <td v-for="column in columns" :key="column" :title="formatValue(row[column])">
                {{ formatCellValue(column, row[column]) }}
              </td>
              <td class="actions-column"><button class="verify-button" type="button" @click="verifyRow(row)" :disabled="getRowId(row) === null || isVerifying">{{ verificationActionLabel }}</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="modal-footer"><button class="cancel-button" type="button" @click="$emit('close')">Close</button></div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VerifyReporting',
  emits: ['close', 'count'],
  data() {
    return { rows: [], searchQuery: '', selectedAutoIds: [], isLoading: false, isVerifying: false, error: '', filterstatus: 'not_verified' };
  },
  computed: {
    columns() {
      const preferred = ['applicantname', 'accountname', 'vesselname', 'voyagename', 'movename', 'locname', 'container_count'];
      const available = Object.keys(this.rows[0] || {}).filter(column => column !== 'selected');
      return preferred.filter(column => available.includes(column));
    },
    filteredRows() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.rows;
      return this.rows.filter(row => Object.values(row).some(value => String(value ?? '').toLowerCase().includes(query)));
    },
    allFilteredRowsSelected() {
      const ids = this.filteredRows.map(row => this.getRowId(row)).filter(id => id !== null);
      return ids.length > 0 && ids.every(id => this.selectedAutoIds.includes(id));
    },
    verificationActionLabel() {
      return this.filterstatus === 'verified' ? 'Not Verify' : 'Verify';
    },
    verificationAction() {
      return this.filterstatus === 'verified' ? 'not_verify' : 'verify';
    }
  },
  mounted() {
    this.fetchReports(this.filterstatus);
  },
  methods: {
    async testRunReporting(){
      this.isLoading = true;
      this.error = '';
      try {
        const token = localStorage.getItem('access_token');
        const config = token ? { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(`auto_run_reporting`, config);
        console.log('Test run reporting response:', response.data);
      } catch (error) {
        console.error('Error testing run reporting:', error);
        this.error = error.response?.data?.message || 'Unable to test run reporting.';
      } finally {
        this.isLoading = false;
      }
    },
    async testRunBilling(){
      this.isLoading = true;
      this.error = '';
      try {
        const token = localStorage.getItem('access_token');
        const config = token ? { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(`auto_run_billing`, config);
        console.log('Test run billing response:', response.data);
      } catch (error) {
        console.error('Error testing run billing:', error);
        this.error = error.response?.data?.message || 'Unable to test run billing.';
      } finally {
        this.isLoading = false;
      }
    },
    async fetchReports(status = 'not_verified') {
      this.isLoading = true;
      this.error = '';
      try {
        const token = localStorage.getItem('access_token');
        const config = token ? { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(`get_report_billing_list?status=${status}`, config);
        const payload = response.data;
        const data = Array.isArray(payload) && payload.length >= 3 ? payload[2] : payload;
        this.rows = this.normalizeRows(data);
        this.selectedAutoIds = [];
        if(status === 'not_verified')
        {
          this.$emit('count', this.rows.length);
        }
      } catch (error) {
        console.error('Error fetching verify reports:', error);
        this.rows = [];
        this.selectedAutoIds = [];
        this.$emit('count', 0);
        this.error = error.response?.data?.message || 'Unable to load verification reports.';
      } finally {
        this.isLoading = false;
      }
    },
    normalizeRows(data) {
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== 'object') return [];
      return data.results || data.data || data.rows || [];
    },
    getRowId(row) {
      const id = row?.autoid;
      return id === undefined || id === null || id === '' ? null : id;
    },
    isSelected(row) {
      const id = this.getRowId(row);
      return id !== null && this.selectedAutoIds.includes(id);
    },
    toggleRow(row) {
      const id = this.getRowId(row);
      if (id === null) return;
      this.selectedAutoIds = this.isSelected(row)
        ? this.selectedAutoIds.filter(selectedId => selectedId !== id)
        : [...this.selectedAutoIds, id];
    },
    toggleSelectAll(event) {
      const ids = this.filteredRows.map(row => this.getRowId(row)).filter(id => id !== null);
      this.selectedAutoIds = event.target.checked
        ? [...new Set([...this.selectedAutoIds, ...ids])]
        : this.selectedAutoIds.filter(id => !ids.includes(id));
    },
    verifyRow(row) {
      const id = this.getRowId(row);
      if (id !== null) this.verifyReports([id], this.verificationAction);
    },
    verifySelected() {
      this.verifyReports(this.selectedAutoIds, this.verificationAction);
    },
    async verifyReports(ids, verifystatus = 'verify') {
      const reportIds = ids.filter(id => id !== null && id !== undefined && id !== '');
      if (reportIds.length === 0) return;
      this.isVerifying = true;
      this.error = '';
      try {
        const token = localStorage.getItem('access_token');
        const config = token ? { headers: { accept: 'application/json', Authorization: `Bearer ${token}` } } : {};
        const formData = new FormData();
        formData.append('transaction', JSON.stringify({ checkedIds: reportIds, verifystatus: verifystatus }));
        const response = await axios.post('/verify_auto_report_transactions', formData, config);
        if (response.data?.[0] === 0) throw new Error(response.data[1] || 'Verification failed');
        await this.fetchReports(this.filterstatus);
      } catch (error) {
        console.error('Error verifying reports:', error);
        this.error = error.response?.data?.message || error.message || 'Unable to verify reports.';
      } finally {
        this.isVerifying = false;
      }
    },
    formatColumn(column) {
      if (column === 'container_count') return 'Containers';
      if (column === 'applicantname') return 'Applicant';
      if (column === 'accountname') return 'Account';
      if (column === 'vesselname') return 'Vessel';
      if (column === 'voyagename') return 'Voyage';
      if (column === 'movename') return 'Movement';
      if (column === 'locname') return 'Location';
      return column.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, value => value.toUpperCase());
    },
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },
    formatCellValue(column, value) {
      const formattedValue = this.formatValue(value);
      if (column === 'locname' && formattedValue !== '-') {
        const location = String(formattedValue);
        return (location.match(/\s/g) || []).length > 2
          ? location.trim().split(/\s+/).map(word => word.charAt(0)).join('')
          : location.slice(0, 12);
      }
      return ['accountname', 'applicantname'].includes(column)
        ? String(formattedValue).slice(0, 10)
        : formattedValue;
    }
  }
};
</script>

<style scoped>
.verify-reporting-modal { background: #fff; width: 95%; max-width: 1100px; border-radius: 8px; overflow: hidden; }
.modal-header, .modal-footer { padding: 12px 16px; border-bottom: 1px solid #eee; }
.modal-header, .header-actions { display: flex; align-items: center; gap: 12px; }
.modal-header { justify-content: space-between; }
.modal-header h3 { margin: 0; }
.header-actions input { width: 260px; max-width: 40vw; padding: 8px 10px; border: 1px solid #ccc; border-radius: 6px; }
.close-btn { background: none; border: none; font-size: 22px; cursor: pointer; }
.modal-body { height: 500px; max-height: 65vh; overflow: auto; }
.state-message { padding: 32px; text-align: center; color: #555; }
.error-message { color: #b42318; }
.reports-table { width: 100%; border-collapse: collapse; }
.reports-table th, .reports-table td { padding: 8px 10px; border: 1px solid #eee; text-align: left; white-space: nowrap; }
.reports-table th { position: sticky; top: 0; background: #f5f5f5; }
.selection-column { width: 42px; text-align: center !important; }
.actions-column { width: 92px; text-align: center !important; }
.verify-button { padding: 6px 10px; border: 0; border-radius: 4px; background: #198754; color: #fff; cursor: pointer; }
.verify-button:disabled { opacity: 0.55; cursor: not-allowed; }
.modal-footer { border-top: 1px solid #eee; border-bottom: 0; text-align: right; }
.cancel-button { padding: 8px 12px; border: 0; border-radius: 4px; background: #6c757d; color: #fff; cursor: pointer; }
</style>